import React, { useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { EqBoosterSlider } from './assets';
import { getLatestDescendantDueDate } from '../utils/taskDueDates';
import Task from '../utils/TaskMaster';
import PriorityPanel from './PriorityPanel';
import CategoryPicker from './CategoryPicker';

const DEFAULT_FIELDS = {
  title: true,
  category: true,
  importance: true,
  frequency: true,
  ordered: true,
  duration: true,
  alarm: true,
  due_date: true,
};

const TaskInputForm = ({ idPrefix = 'task-input', task: initialTask, buttonText, buttonClick,handleClose, fields = {} }) => {
  const displayedDueDate = getLatestDescendantDueDate(initialTask) || initialTask?.due_date || null;
  const [task, setTask] = useState({ ...initialTask, due_date: displayedDueDate });
  const [showDueDateWarning, setShowDueDateWarning] = useState(false);
  const visibleFields = { ...DEFAULT_FIELDS, ...fields };
  const rawPriority = Task.calculatePriority(
    task.importance,
    task.due_date,
    task.creation_date
  );
  const displayedPriority = Task.formatUserViewPriority(rawPriority);
  const finishSubmit = (cascadeDueDate = false) => {
    buttonClick(task, { cascadeDueDate });
    if (handleClose) handleClose(); // Closes the dialog after click
  };

  const handleSubmit = () => {
    const originalTime = displayedDueDate ? new Date(displayedDueDate).getTime() : null;
    const updatedTime = task.due_date ? new Date(task.due_date).getTime() : null;
    if ((task.subtasks || []).length > 0 && originalTime !== updatedTime) {
      setShowDueDateWarning(true);
      return;
    }
    finishSubmit(false);
  };

  const handleChange = (field, value) => {
    setTask((prev) => ({ ...prev, [field]: value }));
  };

  const handleCategoryChange = (categoryId, category) => {
    const applyCategory = (node) => ({
      ...node,
      category_id: categoryId,
      category: category?.name || 'no-category',
      category_color: category?.color || null,
      subtasks: (node.subtasks || []).map(applyCategory),
    });
    setTask((prev) => applyCategory(prev));
  };

  const handleDurationChange = (type, value) => {
    const hours = type === 'hours' ? value : Math.floor((task.duration || 0) / 3600);
    const minutes = type === 'minutes' ? value : Math.floor((task.duration || 0) % 3600 / 60);
    const totalSeconds = hours * 3600 + minutes * 60;
    setTask((prev) => ({ ...prev, duration: totalSeconds }));
  };

  return (
    <Box id={`${idPrefix}-form`} component="form">
      {/* Task Name */}
      {visibleFields.title && (
        <FormControl fullWidth margin="normal">
          <TextField
            id={`${idPrefix}-name`}
            label="Task Name"
            variant="outlined"
            value={task.title || ''}
            onChange={(e) => handleChange('title', e.target.value)}
          />
        </FormControl>
      )}
      {/* Task Category */}
      {visibleFields.category && (
        <FormControl fullWidth margin="normal">
          <CategoryPicker
            idPrefix={`${idPrefix}-category`}
            label="Task Category"
            value={task.category_id ?? null}
            onChange={handleCategoryChange}
          />
        </FormControl>
      )}

      {/* Frequency in Days */}
      {visibleFields.frequency && (
        <FormControl fullWidth margin="normal">
          <TextField
            id={`${idPrefix}-repeat-days`}
            label="Repeat Every (Days)"
            type="number"
            variant="outlined"
            value={task.frequency || 0}
            onChange={(e) => handleChange('frequency', parseInt(e.target.value, 10))}
          />
        </FormControl>
      )}
      {/* Ordered Checkbox */}
      {visibleFields.ordered && (
        <FormControl margin="normal">
          <FormControlLabel
            control={
              <Checkbox
                id={`${idPrefix}-ordered`}
                sx={{ transform: 'scale(2)' }}
                m={2}
                checked={task.ordered || false}
                onChange={(e) => handleChange('ordered', e.target.checked)}
              />
            }
            label="Subtasks need to be completed in order?"
          />
        </FormControl>
      )}
      {task.subtasks.length === 0 && (
        <>
          {/* Duration - Hours */}
          {visibleFields.duration && (
            <FormControl fullWidth margin="normal">
              <InputLabel id="duration-hours-label">Hours</InputLabel>
              <Select
                id={`${idPrefix}-duration-hours`}
                labelId="duration-hours-label"
                value={Math.floor((task.duration || 0) / 3600)}
                onChange={(e) => handleDurationChange('hours', e.target.value)}
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <MenuItem key={i} value={i}>
                    {i}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* Duration - Minutes */}
          {visibleFields.duration && (
            <FormControl fullWidth margin="normal">
              <InputLabel id="duration-minutes-label">Minutes</InputLabel>
              <Select
                id={`${idPrefix}-duration-minutes`}
                labelId="duration-minutes-label"
                value={Math.floor(((task.duration || 0) % 3600) / 60)}
                onChange={(e) => handleDurationChange('minutes', e.target.value)}
              >
                {Array.from({ length: 60 }, (_, i) => (
                  <MenuItem key={i} value={i}>
                    {i}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

        </>
      )}

      {(visibleFields.importance || visibleFields.due_date) && (
        <PriorityPanel
          id="task-form-priority-panel"
          priority={displayedPriority}
          importanceControl={visibleFields.importance ? (
            <FormControl id={`${idPrefix}-importance`} fullWidth>
              <Typography component="label" sx={{ mb: 1 }}>
                Importance
              </Typography>
              <EqBoosterSlider
                value={task.importance ?? 1}
                onChange={(value) => handleChange('importance', value)}
                min={1}
                max={10}
                increments={10}
                minHeight={14}
                maxHeight={48}
                ariaLabel="Importance"
              />
            </FormControl>
          ) : null}
          dueDateControl={visibleFields.due_date ? (
            <FormControl id={`${idPrefix}-due-date`} fullWidth>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  label="Due Date"
                  value={task.due_date ? dayjs(task.due_date) : null}
                  onChange={(newValue) =>
                    handleChange('due_date', newValue ? newValue.toDate() : null)
                  }
                  slotProps={{
                    textField: { id: `${idPrefix}-due-date-input`, fullWidth: true },
                    openPickerButton: { sx: { color: 'primary.main' } },
                  }}
                />
              </LocalizationProvider>
            </FormControl>
          ) : null}
        />
      )}

      {task.subtasks.length === 0 && visibleFields.alarm && (
        <FormControl margin="normal">
          <FormControlLabel
            control={
              <Checkbox
                id={`${idPrefix}-alarm`}
                checked={task.alarm || false}
                onChange={(e) => handleChange('alarm', e.target.checked)}
              />
            }
            label="Enable Alarm When Time is Up"
          />
        </FormControl>
      )}

      <Button id={`${idPrefix}-submit`} onClick={handleSubmit} variant="contained" color="primary" fullWidth>
        {buttonText}
      </Button>

      <Dialog id={`${idPrefix}-due-date-dialog`} open={showDueDateWarning} onClose={() => setShowDueDateWarning(false)}>
        <DialogTitle>Change all due dates?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Changing this due date will change it for all children tasks. Continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button id={`${idPrefix}-due-date-no`} onClick={() => setShowDueDateWarning(false)}>No</Button>
          <Button id={`${idPrefix}-due-date-yes`} variant="contained" onClick={() => finishSubmit(true)} autoFocus>Yes</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TaskInputForm;
